import { Button, Divider, Form, Modal, Tooltip, message, Select } from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { compareObjectPropertyValues } from "utils/utils";
import { useProjectParticipants } from "hooks/project-participants";
import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import CreateSpecSectionModal from "components/submittal-details/create-spec-section";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import { MUTATION_BULK_EDIT_FEATURE_INSTANCES } from "entity-app/graphQL/ciq-feature-mutations";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { gql } from "@apollo/client";
import FeatureSpecSectionDropdown, {
  featureSpecSectionDropdownRenderer
} from "components/feature-spec-section-dropdown";
import ProjectUserParticipantsDropdown from "components/project-participants-dropdown/project-participants-dropdown";
import SelectNotFoundContent from "components/widgets/select-notfound-content";
import { FeatureTypes } from "entity-app/constants";
import {
  UserParticipationType,
  ESubmittalStatus,
  SidepanelStyles
} from "../../../constants";

type TEditSubmittal = {
  type: undefined | number;
  raw_type: string | undefined;
  responsible_contractor: undefined | string;
  submitter: undefined | string | null;
  submitter_unregistered: string | null;
  gc_reviewer: undefined | string | null;
  design_reviewer: string | null;
  design_reviewer_unregistered: string | null;
  design_reviewer_vendor_unregistered: string | null;
  spec_section_id: string | null;
};

export type EditSubmittalLogs = {
  onUpdateDone: any;
  onCancelClick: any;
  selectedRows: Array<any>;
  gqlClient: any;
  showSubmittalEdit: boolean;
  setShowSubmittalEdit: any;
  projectId: string;
};

function BulkEditSubmittalDetailsNewModel(props: EditSubmittalLogs) {
  const {
    onUpdateDone,
    onCancelClick,
    selectedRows,
    gqlClient,
    showSubmittalEdit,
    setShowSubmittalEdit,
    projectId
  } = props;

  // console.log("selectedRows", selectedRows);

  const formattedSelectedRows = useMemo(
    () =>
      selectedRows.map((row) => {
        const wfStatus = row?.workflow_status?.code?.toUpperCase();
        const stateNumeric =
          ESubmittalStatus[wfStatus as keyof typeof ESubmittalStatus];
        const specSectionIds = row.spec_section?.map((x: any) => x.id);
        return {
          ...row,
          spec_section_id:
            specSectionIds && specSectionIds.length > 0
              ? specSectionIds[0]
              : null,
          type: row.type?.id || "",
          raw_type: row.type?.value || "",
          responsible_contractor: row.responsible_contractor?.id || "",
          responsible_contractor_id: row.responsible_contractor?.id || "",
          submitter: row.submitter_reviewer?.id || "",
          submitter_user_id: row.submitter_reviewer?.id || "",
          submitter_unregistered: null,
          gc_reviewer: row.gc_user?.id || "",
          gc_reviewer_user_id: row.gc_user?.id || "",
          design_reviewer: row.design_reviewer?.id || "",
          design_reviewer_user_id: row.design_reviewer?.id || "",
          design_reviewer_unregistered: null,
          design_reviewer_vendor_unregistered: null,
          state_numeric: stateNumeric
        };
      }),
    [selectedRows]
  );

  const createSpecRef = useRef<any>(null);
  const [isDiff, setIsDiff] = useState(false);
  const [initialData, setInitialData] = useState<TEditSubmittal>();
  const [isSaving, setIsSaving] = useState(false);
  const [submittalListIds, setSubmittalListIds] = useState<{
    NotStarted: Array<{ id: string; name: string }>;
    Started: Array<{ id: string; name: string }>;
    Closed: Array<{ id: string; name: string }>;
  }>({
    NotStarted: [],
    Started: [],
    Closed: []
  });

  const [editSubmittalData, setEditSubmittalData] = useState<TEditSubmittal>({
    type: undefined,
    raw_type: undefined,
    responsible_contractor: undefined,
    submitter: undefined,
    submitter_unregistered: null,
    gc_reviewer: undefined,
    design_reviewer: null,
    design_reviewer_unregistered: null,
    design_reviewer_vendor_unregistered: null,
    spec_section_id: null
  });

  const [showCreateSpecSectionModal, setShowCreateSpecSectionModal] =
    useState(false);

  const { data: submittalTypes } = useCIQQuery(QUERY_SUBMITTAL_TYPES, {
    client: gqlClient
  });
  const {
    columnHeaders: { submittalHeaderMap }
  }: TProjectContext = useContext(ProjectContext);

  const { projectParticipants } = useProjectParticipants();

  const [bulkUpdateFeatureInstances] = useCIQMutation(
    gql(MUTATION_BULK_EDIT_FEATURE_INSTANCES),
    {
      client: gqlClient
    }
  );

  useEffect(() => {
    const compareFields = [
      "spec_section_id",
      "type",
      "raw_type",
      "responsible_contractor_id",
      "submitter_user_id",
      "submitter_unregistered",
      "gc_reviewer_user_id",
      "design_reviewer_user_id",
      "design_reviewer_unregistered"
    ];

    const firstElement = {} as any;
    compareFields.forEach((field) => {
      firstElement[field] = formattedSelectedRows[0][field] ?? "";
    });

    if (formattedSelectedRows.length > 1) {
      const compare = compareObjectPropertyValues(
        formattedSelectedRows,
        compareFields.map((x) => ({
          field: x
        }))
      );
      compare.fieldsAsArray.forEach((x) => {
        if (x.diffFound) firstElement[x.field] = undefined;
      });
    }

    const intialValue = {
      spec_section_id: firstElement.spec_section_id,
      type: firstElement.type,
      raw_type: firstElement.raw_type,
      responsible_contractor: firstElement.responsible_contractor_id,
      submitter: firstElement.submitter_user_id,
      submitter_unregistered: firstElement.submitter_unregistered,
      gc_reviewer: firstElement.gc_reviewer_user_id,
      design_reviewer: firstElement.design_reviewer_user_id,
      design_reviewer_unregistered: firstElement.design_reviewer_unregistered,
      design_reviewer_vendor_unregistered: null
    } as TEditSubmittal;
    setInitialData(intialValue);
    setEditSubmittalData(intialValue);

    const closedSubmittals = formattedSelectedRows
      .filter((x: any) => x.state_numeric === ESubmittalStatus.DONE)
      .map((x) => ({ id: x.id, name: x.title }));
    const workflowStartedSubmittals = formattedSelectedRows
      .filter(
        (x: any) =>
          x.state_numeric !== ESubmittalStatus.CREATE &&
          x.state_numeric !== ESubmittalStatus.DONE
      )
      .map((x) => ({ id: x.id, name: x.title }));
    const workflowNotStartedSubmittals = formattedSelectedRows
      .filter((x: any) => x.state_numeric === ESubmittalStatus.CREATE)
      .map((x) => ({ id: x.id, name: x.title }));

    setSubmittalListIds({
      NotStarted: workflowNotStartedSubmittals,
      Started: workflowStartedSubmittals,
      Closed: closedSubmittals
    });
  }, [formattedSelectedRows]);

  useEffect(() => {
    if (initialData) {
      const compare = compareObjectPropertyValues(
        [initialData, editSubmittalData],
        Object.getOwnPropertyNames(initialData).map((x) => ({
          field: x
        }))
      );
      setIsDiff(compare.diffFoundSummary);
    }
  }, [initialData, editSubmittalData]);

  const isDisabled = useMemo(() => {
    return (
      submittalListIds.NotStarted.length + submittalListIds.Started.length === 0
    );
  }, [submittalListIds.NotStarted.length, submittalListIds.Started.length]);

  const onFinish = async () => {
    setIsSaving(true);
    const compare = compareObjectPropertyValues(
      [initialData, editSubmittalData],
      Object.getOwnPropertyNames(initialData).map((x) => ({
        field: x
      }))
    );
    const formData = editSubmittalData as any;

    const bulkUpdatePayload = {
      feature_instance_type_id: FeatureTypes.SUBMITTALS,
      feature_instance_ids: formattedSelectedRows
        .filter((row) => row?.workflow_status?.id !== ESubmittalStatus.DONE)
        .map((item) => item.id),
      updates: {}
    };

    compare.fieldsAsArray.forEach((x) => {
      if (x.diffFound) {
        if (Object.hasOwnProperty.call(formData, x.field)) {
          bulkUpdatePayload.updates = {
            [x.field]: formData[x.field],
            ...bulkUpdatePayload.updates
          };
        }
      }
    });

    const bulkUpdateResults = await bulkUpdateFeatureInstances({
      variables: bulkUpdatePayload
    });

    if (bulkUpdateResults.success) {
      message.success("Submittals are updated successfully");
      onUpdateDone();
    }
  };

  const fieldsForStartedSubmittalOnly = (
    <div>
      {submittalListIds.Started.length > 0 &&
        submittalListIds.NotStarted.length > 0 && (
          <div className="py-3">
            <Divider className="px-0 mt-0 mb-1 pb-1" />

            <div className="text-xs">
              The fields below will only be updated in submittals for which
              workflow has not started.
              <DisplayNameListPopover list={submittalListIds.NotStarted}>
                {submittalListIds.NotStarted.length}
              </DisplayNameListPopover>
              out of
              <DisplayNameListPopover
                list={[
                  ...submittalListIds.NotStarted,
                  ...submittalListIds.Started
                ]}
              >
                {submittalListIds.NotStarted.length +
                  submittalListIds.Started.length}
              </DisplayNameListPopover>
              submittals selected, will be updated.
            </div>
          </div>
        )}

      {submittalListIds.NotStarted.length > 0 && (
        <div className="space-y-3">
          <Form.Item label={submittalHeaderMap?.responsible_contractor}>
            <Select
              showSearch
              disabled={submittalListIds.NotStarted.length === 0}
              value={
                projectParticipants?.dataLoaded
                  ? editSubmittalData.responsible_contractor
                  : null
              }
              optionFilterProp="label"
              className="constructionSelect"
              onChange={(value) => {
                setEditSubmittalData((pre) => ({
                  ...pre,
                  responsible_contractor: value,
                  submitter: null,
                  submitter_unregistered: null
                }));
              }}
              notFoundContent={
                <SelectSearchNotFoundContent
                  notFoundMsg={
                    projectParticipants?.responsibleContractors?.length > 0
                      ? "Company not found. To add, go to "
                      : "No companies added to this project. To add, go to "
                  }
                  linkTitle="Project Companies"
                  linkPath={`/project/${projectId}/settings/general/project-companies`}
                />
              }
              options={
                projectParticipants
                  ? projectParticipants?.responsibleContractors.map(
                      (company: any) => ({
                        label: company.subscription_vendor.name,
                        value: company.vendor_id
                      })
                    )
                  : []
              }
            />
          </Form.Item>
          <Tooltip
            title={
              editSubmittalData.responsible_contractor
                ? ""
                : "First select a Responsible Contractor"
            }
            placement="rightBottom"
          >
            <Form.Item label={submittalHeaderMap?.submitter}>
              <ProjectUserParticipantsDropdown
                userParticipationType={UserParticipationType.SUBMITTER}
                value={
                  editSubmittalData.submitter ||
                  editSubmittalData.submitter_unregistered
                }
                selectedCompanyId={editSubmittalData.responsible_contractor}
                onChange={(value: string) => {
                  const selectedUser = projectParticipants?.submitterUsers.find(
                    (user: any) => user.id === value
                  );
                  if (selectedUser.type === "actual") {
                    setEditSubmittalData((pre) => ({
                      ...pre,
                      submitter: value,
                      submitter_unregistered: null
                    }));
                  } else {
                    setEditSubmittalData((pre) => ({
                      ...pre,
                      submitter_unregistered: value,
                      submitter: null
                    }));
                  }
                }}
                showSearch
                optionFilterProp="label"
                filterOption
                disabled={!editSubmittalData.responsible_contractor}
                notFoundContent={
                  <SelectSearchNotFoundContent
                    notFoundMsg={
                      projectParticipants.submitterUsers?.length > 0
                        ? "User is not part of above selected company. To add, go to "
                        : "There are no users added to this company. To add, go to "
                    }
                    linkTitle="Project Settings"
                    linkPath={`/project/${projectId}/settings/general/project-users`}
                  />
                }
              />
            </Form.Item>
          </Tooltip>
          <Form.Item label={submittalHeaderMap?.gc_reviewer}>
            <ProjectUserParticipantsDropdown
              userParticipationType={UserParticipationType.GC_REVIEWER}
              value={editSubmittalData.gc_reviewer}
              onChange={(value: string) => {
                setEditSubmittalData((pre) => ({
                  ...pre,
                  gc_reviewer: value
                }));
              }}
              notFoundContent={
                <SelectNotFoundContent
                  notFoundMsg="User does not exist in the project. Go to Project Settings to add new user."
                  onAddClick={() => {}}
                  showAddBtn={false}
                />
              }
            />
          </Form.Item>
          <Form.Item label={submittalHeaderMap?.design_reviewer}>
            <ProjectUserParticipantsDropdown
              userParticipationType={UserParticipationType.DESIGN_REVIEWER}
              value={
                editSubmittalData.design_reviewer ||
                editSubmittalData.design_reviewer_unregistered
              }
              onChange={(value: string) => {
                const foundUser: any = projectParticipants.designReviewers.find(
                  (user: any) => {
                    return user.id === value;
                  }
                );
                if (foundUser.type === "actual") {
                  setEditSubmittalData((pre) => ({
                    ...pre,
                    design_reviewer: value,
                    design_reviewer_unregistered: null,
                    design_reviewer_vendor_unregistered: null
                  }));
                } else {
                  setEditSubmittalData((pre) => ({
                    ...pre,
                    design_reviewer_unregistered: value,
                    design_reviewer_vendor_unregistered: foundUser.company.name,
                    design_reviewer: null
                  }));
                }
              }}
              showSearch
              filterOption
              optionFilterProp="label"
              notFoundContent={
                <SelectSearchNotFoundContent
                  notFoundMsg={
                    projectParticipants.designReviewers?.length > 0
                      ? "Architect/Engineer is not added to project. To add, go to "
                      : "There are no Architects/Engineers added to this project. To add, go to "
                  }
                  linkTitle="Project Settings"
                  linkPath={`/project/${projectId}/settings/general/project-users`}
                />
              }
              disabled={submittalListIds.NotStarted.length === 0}
            />
          </Form.Item>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title="Bulk Edit Details"
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      footer={[
        <section className="submittal-modal-footer " key={100}>
          <Button onClick={onCancelClick} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={!isDiff || isSaving}
            loading={isSaving}
            onClick={onFinish}
          >
            Apply
          </Button>
        </section>
      ]}
      open={showSubmittalEdit}
      onCancel={() => {
        setShowSubmittalEdit(false);
      }}
      destroyOnClose
    >
      <div className="pb-3 px-3 -mt-2">
        <div className="font-semibold">
          <span className="tracking-wide">Selected Submittals:</span>{" "}
          {formattedSelectedRows.length}
        </div>
        {submittalListIds.Closed.length > 0 ? (
          <div className="mt-2 text-xs">
            Closed submittals will not be updated.
            <DisplayNameListPopover
              list={[
                ...submittalListIds.NotStarted,
                ...submittalListIds.Started
              ]}
            >
              {submittalListIds.NotStarted.length +
                submittalListIds.Started.length}
            </DisplayNameListPopover>
            out of
            <DisplayNameListPopover
              list={[
                ...submittalListIds.NotStarted,
                ...submittalListIds.Started,
                ...submittalListIds.Closed
              ]}
            >
              {submittalListIds.NotStarted.length +
                submittalListIds.Started.length +
                submittalListIds.Closed.length}
            </DisplayNameListPopover>
            Submittals selected, will be updated.
          </div>
        ) : null}
      </div>
      <Form
        layout="vertical"
        className="text-base space-y-3 px-3"
        disabled={showCreateSpecSectionModal}
      >
        <div className="flex-col overflow-y overflow-x-hidden h-full px-1">
          <Form.Item label={submittalHeaderMap?.spec_no}>
            <FeatureSpecSectionDropdown
              disabled={isDisabled}
              selectedIds={
                editSubmittalData.spec_section_id
                  ? [editSubmittalData.spec_section_id]
                  : []
              }
              onBlur={(ids) => {
                setEditSubmittalData((pre) => ({
                  ...pre,
                  spec_section_id: ids.length > 0 ? ids[0] : null
                }));
              }}
              dropdownRender={(menu) =>
                featureSpecSectionDropdownRenderer(menu, () => {
                  setShowCreateSpecSectionModal(true);
                })
              }
              mode="single"
            />
          </Form.Item>
          <Form.Item label={submittalHeaderMap?.type}>
            <Select
              optionFilterProp="label"
              value={editSubmittalData.type}
              onChange={(value) => {
                const type = Number(value);
                const typeObj = submittalTypes?.configurations_value?.find(
                  (t: any) => t.id === type || t.value === type
                );
                setEditSubmittalData((pre) => ({
                  ...pre,
                  type: typeObj?.id,
                  raw_type: typeObj?.value
                }));
              }}
              disabled={isDisabled}
              options={
                submittalTypes
                  ? submittalTypes.configurations_value.map((type: any) => ({
                      value: type.id,
                      label: type.value
                    }))
                  : []
              }
            />
          </Form.Item>
          {fieldsForStartedSubmittalOnly}
        </div>
      </Form>
      {showCreateSpecSectionModal && (
        <div ref={createSpecRef}>
          <Divider style={{ margin: "15px 0" }} />
          <div>
            <CreateSpecSectionModal
              isModalOpen={showCreateSpecSectionModal}
              onDoneCb={(newSpecSectionId: string, name: any, number: any) => {
                if (newSpecSectionId) {
                  setEditSubmittalData((pre) => ({
                    ...pre,
                    spec_section_id: newSpecSectionId,
                    spec_no: number,
                    spec_name: name
                  }));
                  setShowCreateSpecSectionModal(false);
                } else {
                  setShowCreateSpecSectionModal(false);
                }
              }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default BulkEditSubmittalDetailsNewModel;
